import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filterOutUndefined } from '@shared/rxjs/filter-out-null.operator';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ProductType } from '../models/api/dsm-types';
import { LogService } from '../services/log.service';
import { AppConfigService } from '../services/app-config.service';
import { StringUtils } from '@shared/utils/string.utils';
import { getAccessToken } from '../store/entities/session/session.selector';
import { ObjectValidator } from '@core/helper/object-validator';
import { SearchQuoteResult } from '@core/interfaces/interfaces';

export interface QuoteTransferRequest {
  producer: ProducerTransfer;
  existingAccountNumber?: string;
}

export interface ProducerTransfer {
  producerCode: string;
}

export interface QuoteTransferResponse {
  quoteId: string;
  accountNumber: string;
}

export function sanitizeQuoteTransferResponse(
  input: unknown
): QuoteTransferResponse {
  return ObjectValidator.forceSchema<QuoteTransferResponse>(input, {
    quoteId: 'string',
    accountNumber: 'string',
  });
}

@Injectable({
  providedIn: 'root',
})
export class QuoteTransferAdapter {
  accessToken = '';
  targetEnv = '';

  constructor(
    private httpClient: HttpClient,
    private store: Store,
    private appConfig: AppConfigService,
    private log: LogService
  ) {
    this.store
      .select(getAccessToken)
      .pipe(filterOutUndefined())
      .subscribe((token) => (this.accessToken = token));
  }

  genNwHeaders(correlationId: string): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', `Bearer ${this.accessToken}`);
    headers = headers.set('client_id', this.appConfig.config.apiKey);
    headers = headers.set('X-NW-Message-ID', correlationId);
    headers = headers.set('Distribution-Partner-Id', 'Default');
    if (this.appConfig.config.targetEnv) {
      headers = headers.set('X-NW-Target-Env', this.appConfig.config.targetEnv);
    }
    return headers;
  }

  transfer(
    quoteId: string,
    producerCode: string,
    productType: ProductType
  ): Observable<QuoteTransferResponse> {
    if (!quoteId?.trim()) {
      throw new Error('QuoteId is required for transfer');
    }
    if (!producerCode?.trim()) {
      throw new Error('ProducerCode is required for transfer');
    }
    return this._transfer(quoteId, producerCode, productType);
  }

  private _transfer(
    quoteId: string,
    producerCode: string,
    productType: ProductType
  ): Observable<QuoteTransferResponse> {
    const url = `${this.appConfig.config.quoteAccountManagement}/quotes/${quoteId}/quote-transfers`;

    const requestPayload: QuoteTransferRequest = {
      producer: {
        producerCode,
      },
    };
    const correlationId = this.log.logApiRequest(
      'POST',
      url,
      'transfer-quote',
      requestPayload
    );

    return this.httpClient
      .post<QuoteTransferResponse>(url, requestPayload, {
        headers: this.genNwHeaders(correlationId),
      })
      .pipe(
        tap((response) => {
          this.log.logApiResponse(correlationId, 200, response);
        }),
        map((response) => sanitizeQuoteTransferResponse(response)),
        catchError((error) => this.log.logApiError(correlationId, error))
      );
  }

  transferToExistingAccount(
    quoteId: string,
    producerCode: string,
    productType: ProductType,
    existingAccountNumber: string
  ): Observable<QuoteTransferResponse> {
    if (!quoteId?.trim()) {
      throw new Error('QuoteId is required for transfer');
    }
    if (!producerCode?.trim()) {
      throw new Error('ProducerCode is required for transfer');
    }
    if (!existingAccountNumber?.trim()) {
      throw new Error(
        'ExistingAccountNumber is required for transfer to existing account'
      );
    }
    return this._transferToExistingAccount(
      quoteId,
      producerCode,
      productType,
      existingAccountNumber
    );
  }

  private _transferToExistingAccount(
    quoteId: string,
    producerCode: string,
    productType: ProductType,
    existingAccountNumber: string
  ): Observable<QuoteTransferResponse> {
    const url = `${this.appConfig.config.quoteAccountManagement}/quotes/${quoteId}/quote-transfers`;

    const requestPayload: QuoteTransferRequest = {
      producer: {
        producerCode,
      },
      existingAccountNumber,
    };
    const correlationId = this.log.logApiRequest(
      'POST',
      url,
      'transfer-quote',
      requestPayload
    );

    return this.httpClient
      .post<QuoteTransferResponse>(url, requestPayload, {
        headers: this.genNwHeaders(correlationId),
      })
      .pipe(
        tap((response) => {
          this.log.logApiResponse(correlationId, 200, response);
        }),
        map((response) => sanitizeQuoteTransferResponse(response)),
        catchError((error) => this.log.logApiError(correlationId, error))
      );
  }
}
